import React, { useContext, useState, useEffect, useMemo } from 'react';
import { get, map, every, some } from 'lodash';
import './SignDocReports.scss';
import { SignDocReportCard } from '../SignDocReportCard/SignDocReportCard';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { useUserAdminOrgs } from '../../Hooks/UseUserAdminOrgs';
import { OrganizationFilter } from '../../../Utils/GridFilters/OrganizationFilter/OrganizationFilter';
import { SignDocTypeFilter } from '../../../Utils/GridFilters/TypeFilter/SignDocTypeFilter';
import { SearchFilter } from '../../../Utils/GridFilters/SearchFilter/SerachFilter';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { getSignDocReportsAPI } from '../../../Services/api';
import { getOrganizationalUnitsAssignedToSignDocType } from '../../../Utils/SignDocs/SignDocs';
import { getAvailableTypeOptions, VIDEO_TYPE, VIDEO_TYPES } from '../../SignDoc/SignDocLib';
import { CompanyReactContext } from '../../../contexts/CompanyContext';
import { getAdminCreatedTypes } from '../../../Utils/TabFiltering/TabFilteringUtils';
import { getSignDocsOrganizationUsersCSVApi } from '../../../Services/api';
import { Button } from '../../Form/Button/Button';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFilterPersistence, generateFilterStorageKey } from '../../../Hooks/useFilterPersistence';
import useDebounce from '../../../Hooks/useDebounce';

/**
 * Main container for SignDoc reports - displays any SignDoc type reports (courses, exams, surveys, etc.)
 * Features organization filtering and displays SignDoc data using SignDocReportCard components
 *
 * @param {object} params - Component parameters
 * @param {Array} params.types - SignDoc types to display (ONLINE_COURSE, FRONTAL_COURSE, EXAM, SURVEY, etc.)
 * @param {Array} [params.categories] - SignDoc categories to filter by (FORCE_COMPLETION, CERTIFICATION, KNOWLEDGE_CENTER)
 * @param {boolean} [params.forceCompletion=true] - Whether to force completion of SignDocs
 * @returns {JSX.Element} SignDoc reports container component
 */
export const SignDocReports = ({ types, categories, forceCompletion=true }) => {
    const { i18n } = useContext(I18nReactContext);
    const companyContext = useContext(CompanyReactContext);
    const location = useLocation();
    const { adminOrganizationOptions, adminOrganizations, isOrgsLoading } = useUserAdminOrgs({
        withAllOption: true,
        withCoursesAndSignDocs: true
    });

    // Generate unique storage key based on current route path
    const storageKey = useMemo(() => {
        return generateFilterStorageKey({basePath: location.pathname});
    }, [location.pathname]);

    // Default filter values for persistence
    const defaultFilterValues = useMemo(() => ({
        organizationIds: ['all'],
        types: [],
        search: ''
    }), []);

    // Get persisted filter values from sessionStorage
    const { filterValues: persistedFilterValues, setFilterValues } = useFilterPersistence({
        storageKey,
        defaultFilterValues
    });

    // State for filtering and data - initialize with persisted values
    const [orgsFilterModel, setOrgsFilterModel] = useState({
        filterData: {
            organizations: [],
            types: [] // Available type options
        },
        filterValues: persistedFilterValues
    });
    const [signDocsWithReportData, setSignDocsWithReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [availableTypeOptions, setAvailableTypeOptions] = useState([]);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const navigate = useNavigate();

    // Debounce filter values to prevent excessive API calls when user rapidly changes filters
    const debouncedFilterValues = useDebounce({value: orgsFilterModel.filterValues, delay: 500});

    // Sync filter values to sessionStorage when they change
    useEffect(() => {
        setFilterValues(orgsFilterModel.filterValues);
    }, [orgsFilterModel.filterValues, setFilterValues]);

    /**
     * Load available type options based on categories
     * Fetches SignDocs with the specified categories and extracts their types
     */
    useEffect(() => {
        const loadAvailableTypes = async () => {
            try {
                // Get types that exist for the given categories
                const existingTypes = await getAdminCreatedTypes({
                    categories: categories,
                    isForReportsComponent: true
                });

                // Convert to type options format with feature flag filtering
                const typeOptions = getAvailableTypeOptions({
                    types: types,
                    filterableTypes: existingTypes, // Only show types that exist for these categories
                    featureFlags: companyContext.featureFlags || {},
                    i18n: i18n
                });
                setAvailableTypeOptions(typeOptions);
            } catch (error) {
                console.error('Error loading available types:', error);
                setAvailableTypeOptions([]);
            }
        };

        loadAvailableTypes();
    }, [categories, types, i18n, companyContext.featureFlags]);

    /**
     * Fetches SignDoc reports data from the API using debounced filter values
     * @returns {void}
     */
    const fetchSignDocReportsData = async () => {
        try {
            setIsLoading(true);

            // Always use organizationIds format for consistency
            const selectedOrgIds = get(debouncedFilterValues, 'organizationIds', ['all']);

            // Get selected types from filter model (empty array = all types)
            const selectedTypes = get(debouncedFilterValues, 'types', []);

            // Determine which types to fetch: selected types if any, otherwise all types
            let typesToFetch = selectedTypes.length > 0 ? selectedTypes : types;

            // Expand 'VIDEOS' to individual video types for backend
            // Backend doesn't know about the consolidated 'VIDEOS' type
            typesToFetch = typesToFetch.flatMap(type =>
                type === VIDEO_TYPE ? VIDEO_TYPES : [type]
            );

            // Call the API to get SignDoc reports data
            const signDocsWithReportData = await getSignDocReportsAPI({
                types: typesToFetch,
                organizationIds: selectedOrgIds,
                categories: categories
            });

            setSignDocsWithReportData(signDocsWithReportData || []);
        } catch (error) {
            console.error('Error fetching SignDoc reports data:', error);
            setSignDocsWithReportData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Initialize organization options - preserve persisted filter values if available
    useEffect(() => {
        const filterModelInit = async() => {
            if (adminOrganizationOptions && Array.isArray(adminOrganizationOptions) && adminOrganizationOptions.length > 0) {
                const orgOptions = await getOrganizationalUnitsAssignedToSignDocType({
                    signDocTypes: types,
                    categories: categories, // Pass categories to filter organizations
                    showForceCompletion: forceCompletion,
                    showNonForceCompletion: !forceCompletion,
                    asOptions: true,
                    withAllOption: true
                });

                setOrgsFilterModel(prev => {
                    // Use persisted organizationIds if they exist and ALL are valid, otherwise use first option
                    const persistedOrgIds = prev.filterValues.organizationIds;
                    const hasValidPersistedOrgs = persistedOrgIds &&
                        persistedOrgIds.length > 0 &&
                        orgOptions.length > 0 &&
                        (persistedOrgIds.includes('all') ||
                         every(persistedOrgIds, id => some(orgOptions, opt => opt.value === id)));

                    return {
                        filterData: {
                            ...prev.filterData,
                            organizations: orgOptions,
                        },
                        filterValues: {
                            ...prev.filterValues,
                            organizationIds: hasValidPersistedOrgs
                                ? persistedOrgIds
                                : [get(orgOptions, '[0].value', 'all')]
                        }
                    };
                });
            }
        };
        filterModelInit();
    }, [adminOrganizationOptions, types, forceCompletion, categories]);

    // Update available types when they are loaded
    useEffect(() => {
        if (availableTypeOptions) {
            setOrgsFilterModel(prev => ({
                ...prev,
                filterData: {
                    ...prev.filterData,
                    types: availableTypeOptions
                }
            }));
        }
    }, [availableTypeOptions]);

    // Fetch SignDoc reports data when organizations are loaded or debounced filter values change
    useEffect(() => {
        // Only fetch when we have proper organization data loaded (not just initial state)
        if (adminOrganizations &&
            debouncedFilterValues &&
            orgsFilterModel?.filterData?.organizations?.length > 0 &&
            types?.length > 0) {
            fetchSignDocReportsData();
        }
    }, [adminOrganizations, debouncedFilterValues, types]);

    /**
     * Apply client-side filtering to SignDoc reports data
     * Filters by search term (searches in docName and description)
     */
    const filteredSignDocs = useMemo(() => {
        let filtered = signDocsWithReportData;

        // Apply search filter if present
        const searchTerm = get(orgsFilterModel, 'filterValues.search', '');
        if (searchTerm && searchTerm.trim() !== '') {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = filtered.filter((signDoc) => {
                const docName = get(signDoc, 'docName', '');
                const description = get(signDoc, 'description', '');
                return docName.toLowerCase().includes(searchLower) ||
                       description.toLowerCase().includes(searchLower);
            });
        }

        return filtered;
    }, [signDocsWithReportData, orgsFilterModel]);

    const handleDownloadReport = async () => {
        try {
            setIsGeneratingReport(true);
            await getSignDocsOrganizationUsersCSVApi({
                paginationModel: { page: 0, pageSize: 0 },
                filterModel: orgsFilterModel,
                sortModel: []
            });
            
            navigate('/reports/generatedReports');
        } catch (error) {
            console.error('Error generating report:', error);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    // Show loading spinner while organizations are loading
    if (isOrgsLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="SignDocReports">
            <div className="filtersSection">
                {/* Search Filter */}
                <SearchFilter
                    filterModel={orgsFilterModel}
                    setFilterModel={setOrgsFilterModel}
                    label={i18n.translate('signDocReports.searchLabel')}
                    placeholder={i18n.translate('signDocReports.searchPlaceholder')}
                />

                {/* Type Filter */}
                {orgsFilterModel?.filterData?.types?.length > 0 && (
                    <SignDocTypeFilter
                        filterModel={orgsFilterModel}
                        setFilterModel={setOrgsFilterModel}
                        availableTypes={availableTypeOptions}
                        label={i18n.translate('signDocReports.filterByType')}
                    />
                )}

                {/* Organization Filter */}
                {orgsFilterModel?.filterData?.organizations?.length > 0 && (
                    <OrganizationFilter
                        filterModel={orgsFilterModel}
                        setFilterModel={setOrgsFilterModel}
                        enableMultiSelect={true}
                        enableSelectAll={true}
                    />
                )}

                <Button
                    text={i18n.translate('courseReports.downloadReport')}
                    icon={<CloudDownloadIcon />}
                    onClick={handleDownloadReport}
                    loading={isGeneratingReport}
                    disabled={isGeneratingReport}
                    color="yellow"
                />
            </div>

            {/* Content section */}
            <div className="signDocReportsContent">
                {isLoading ? (
                    <LoadingSpinner />
                ) : filteredSignDocs.length > 0 ? (
                    <div className="signDocReportsList">
                        {map(filteredSignDocs, (signDocReportData) => {
                            return (
                                <SignDocReportCard
                                    key={signDocReportData.id}
                                    signDoc={signDocReportData}
                                    questionnaireReportData={get(signDocReportData, 'reportData', {})}
                                    organizationIds={get(orgsFilterModel, 'filterValues.organizationIds', ['all'])}
                                    adminOrganizationOptions={adminOrganizationOptions}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="noSignDocsMessage">
                        <h3>{i18n.translate('signDocReports.noSignDocs.title')}</h3>
                        <p>{i18n.translate('signDocReports.noSignDocs.message')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
